// Clip recorder: capture a short cinematic video of the live bonsai for sharing.
//
// Mirrors gallery.js / settings.js: owns its own state and registers a window.*
// handler (window.recordBonsai) for the inline onclick in the settings drawer.
// main.js pulls it in via a side-effect `import './recorder.js'`.
//
// Flow: click → a 3·2·1 countdown overlay (Skip to start now, Cancel to bail) →
// record. While recording we show a "● REC" badge with a Cancel button.
//
// How the capture works: each frame we draw the live WebGL canvas into an offscreen
// 2D "compositor" canvas, stamp the "bonsaibox" brand watermark on top, and capture
// *that* canvas — so the watermark is baked into the clip (every reshare advertises
// the app). Reading the WebGL canvas via drawImage needs renderer.preserveDrawingBuffer
// (set in main.js). Only canvas pixels are captured — DOM chrome (the countdown, the
// drawer, the REC badge/buttons) never appears in the clip — so we're free to show UI
// during the take. While recording we take over the camera for a cinematic move (see
// startCameraMove) and restore the user's framing afterward.
//
// Output format — two capture backends, picked per browser so the file is broadly
// shareable (Instagram, Adobe Express, etc. reject .webm):
//   • MediaRecorder (the simple path): Chrome/Edge/Safari can record H.264 .mp4
//     directly, so we hand the compositor's captureStream() to MediaRecorder.
//   • WebCodecs + mp4-muxer (the Firefox path): Firefox's MediaRecorder only does
//     .webm, but its VideoEncoder can encode H.264 — so we feed each composited
//     frame to a VideoEncoder and mux the chunks into .mp4 with the tiny mp4-muxer
//     library (no transcoder/ffmpeg.wasm). Used only when MediaRecorder *can't* give
//     us mp4 AND a real test-encode succeeds (Firefox's isConfigSupported() lies on
//     some Linux setups, so we probe with an actual encode during the countdown).
//   • Otherwise we fall back to MediaRecorder .webm, same as before.
// The download is named after the tree, with the extension matching what was produced.

import * as THREE from 'three';
import { app } from './core.js';
import { notify } from './gallery.js';
import { playSfx } from './audio.js';

// 'idle' | 'countdown' | 'recording'. Guards re-entry and tells the stop path
// whether there's anything to tear down.
let phase = 'idle';

const COUNTDOWN_FROM = 3;
const CLIP_SECONDS = 10;
const FPS = 60;
const FRAME_DUR_US = Math.round(1e6 / FPS); // nominal frame duration (µs) for WebCodecs chunks

// Capture quality. The app renders at pixel ratio 1 (see main.js — no setPixelRatio),
// so on hi-DPI screens the canvas backing store is below physical resolution and the
// clip looks soft. For the take we temporarily raise the renderer's pixel ratio to
// hit RECORD_HEIGHT vertical pixels (capped for GPU/file sanity), then restore it.
// A high bitrate keeps VP9 from reintroducing blockiness at that resolution.
const RECORD_HEIGHT = 1440;          // target vertical resolution for the capture
const RECORD_MAX_DPR = 3;            // safety cap on the temporary pixel ratio
const RECORD_BITS_PER_SEC = 28_000_000; // ~28 Mbps — crisp at 1440p, file stays reasonable

// Camera move for the take — THREE edited shots with hard cuts between them, for an
// "edited video" feel rather than one long drifting move:
//   Shot 1 (establishing): a wide vantage from further out and a touch elevated,
//     viewed from a distinct angle, holding with a slow drift.
//   — hard cut —
//   Shot 2 (the reveal): snap to the push-in framing, then one continuous sweeping
//     orbit that closes in and cranes low for a hero angle.
//   — hard cut —
//   Shot 3 (the settle): a calm, composed three-quarter portrait at a restful
//     mid-distance, nearly static — let the foliage sway and falling leaves carry
//     the motion. Resolves the rhythm (movement → movement → stillness) and frames
//     the watermark like an end-card. On loop, it cuts cleanly back to the wide.
// Each cut is just the position discontinuity at a phase boundary (no blend).
// Within each shot, sine easing keeps motion graceful but never crawling.
const ESTABLISH_FRAC = 0.22;       // shot 1's slice of the clip; then a hard cut to shot 2
const ESTABLISH_RADIUS = 2.2;      // establishing distance ×: pulled well back for a wide view
const ESTABLISH_RADIUS_END = 2.05; // slow drift inward across the establishing hold
const ESTABLISH_AZ_DEG = -42;      // viewed from a distinct angle vs shot 2, so the cut reveals a new side
const ESTABLISH_DRIFT_DEG = 12;    // gentle azimuth drift across the establishing shot
const ESTABLISH_LIFT = 0.28;       // a touch elevated (radians) — classic establishing look-down
const PUSH_START = 1.4;            // shot 2 opens here (the push-in's starting framing)
const PUSH_FRAC = 0.3;             // fraction of SHOT 2 spent pushing in (orbit/crane fill the rest)
const ORBIT_SWEEP_DEG = 250;       // total azimuth swept during shot 2 — a big arc
const DOLLY_NEAR = 0.72;           // close framing reached after the push-in
const DOLLY_END = 0.68;            // …then held through the orbit
const SETTLE_FRAC = 0.285;          // shot 3's slice — the calm closing hold, cut from the end
const SETTLE_RADIUS = 0.85;        // composed mid-distance × — intimate but not crowding
const SETTLE_RADIUS_END = 1.08;    // a whisper of a push-in across the hold, so it breathes
const SETTLE_AZ_DEG = 30;          // a fresh, composed three-quarter angle for the closer
const SETTLE_DRIFT_DEG = 6;        // barely-there drift — let the foliage do the moving
const SETTLE_LIFT = 0.08;          // a touch elevated (radians) — a restful, settled look
const POLAR_RISE = 0.1;            // faint early lift (radians), via sin — rises then returns
const POLAR_DROP_DEG = 80;     // how far the camera cranes DOWN by the end (degrees) — heroic up-angle
const DROP_START = 0.45;       // fraction of the clip at which the final descent begins
const MIN_CAM_Y = -12;         // ground-safe floor: never crane the camera below this world height

// Sine ease-in-out: the gentlest standard ease — accelerates and decelerates
// softly but never crawls at the ends, so motion stays sustained and sweeping.
// Drives both the opening zoom and the orbit, so the whole move opens with one
// graceful ramp rather than a hard start. t in [0,1].
function easeInOutSine(t) {
  return 0.5 * (1 - Math.cos(Math.PI * t));
}

// Sine ease-in (no ease-out): slow, gentle start that keeps accelerating to full
// speed at the end — so the establishing drift is still moving when the cut hits,
// rather than decelerating to a near-stop first. t in [0,1].
function easeInSine(t) {
  return 1 - Math.cos(t * Math.PI / 2);
}

// The brand watermark: matches #app-title's font, lower-right, subtly transparent.
const WATERMARK_TEXT = 'BonsaiBox 型木';
const WATERMARK_FONT = "'Shippori Antique B1', serif";

// Live recording state, cleared back to null by cleanup().
let recorder = null;
let stream = null;
let compositor = null;   // offscreen 2D canvas we draw WebGL frame + watermark into
let compCtx = null;
let drawRAF = 0;         // rAF id for the per-frame composite loop
let cameraRAF = 0;       // rAF id for the per-frame camera move
let countdownTimer = null;
let stopTimer = null;
let canceled = false;
let restoreControls = null; // { enabled, autoRotate, position } captured at record start
let prevPixelRatio = null;  // renderer pixel ratio to restore after the high-res take

// Capture backend for the in-flight take: 'mr' (MediaRecorder) or 'wc' (WebCodecs).
// Tells finishCapture()/abort()/cleanup() which teardown path to run.
let backend = null;
// WebCodecs state (Firefox mp4 path), cleared by cleanup().
let encoder = null;       // VideoEncoder turning composited frames into H.264 chunks
let muxer = null;         // mp4-muxer Muxer collecting those chunks into an .mp4
let encodeError = null;   // first error from the encoder/muxer; aborts the take
let wcStartTime = 0;      // performance.now() at the first encoded frame (timestamp base)
let wcLastTs = -1;        // last frame timestamp (µs) — kept strictly increasing
let wcFrameCount = 0;     // frames encoded so far (drives the keyframe cadence)
let h264Probe = null;     // Promise<boolean>: can this browser really H.264-encode?

// The H.264 codec string for the encoder. High profile, level 5.1 — covers up to
// 4K, comfortably above our 1440p capture. macOS Firefox routes this through the
// system VideoToolbox encoder; Chrome/Safari never reach this path (MediaRecorder
// already gives them mp4).
const H264_CODEC = 'avc1.640033';

// Probe whether the browser can actually *encode* H.264 via WebCodecs. We can't
// trust VideoEncoder.isConfigSupported() — on some Linux/Firefox builds it reports
// support but the encoder then errors. So we configure a throwaway encoder and feed
// it one real frame: resolving true only once a chunk actually comes out. Run during
// the 3·2·1 countdown, so the result is ready by the time recording begins.
function probeH264Encoder() {
  if (typeof window.VideoEncoder !== 'function' || typeof window.VideoFrame !== 'function') {
    return Promise.resolve(false);
  }
  return new Promise((resolve) => {
    let settled = false;
    let enc = null;
    const done = (ok) => {
      if (settled) return;
      settled = true;
      try { enc?.close(); } catch { /* already errored/closed */ }
      resolve(ok);
    };
    try {
      // Pass only if the encoder actually emits an avcC decoder description — i.e. a
      // chunk mp4-muxer can mux. Firefox can produce H.264 chunks with no description
      // (Annex-B), which would pass a naive "got a chunk" probe but fail to mux.
      enc = new VideoEncoder({
        output: (chunk, meta) => done(!!meta?.decoderConfig?.description),
        error: () => done(false),
      });
      enc.configure({ codec: H264_CODEC, width: 64, height: 64, bitrate: 1_000_000, framerate: 30, avc: { format: 'avc' } });
      const probeCanvas = document.createElement('canvas');
      probeCanvas.width = 64; probeCanvas.height = 64;
      const frame = new VideoFrame(probeCanvas, { timestamp: 0 });
      enc.encode(frame, { keyFrame: true });
      frame.close();
      enc.flush().catch(() => done(false));
    } catch {
      done(false);
    }
    // Safety net: if neither output nor error fires, treat it as unsupported.
    setTimeout(() => done(false), 1500);
  });
}

// Pick the best container/codec the browser will actually record. Prefer mp4
// (H.264) because Instagram, Adobe Express, etc. accept it where webm is often
// rejected — modern Chrome/Edge can record it natively, and Safari only does mp4.
// Fall back to webm (Firefox's MediaRecorder still can't do mp4) and then the bare
// type so older engines still get *something* rather than a hard failure.
function pickMimeType() {
  const candidates = [
    'video/mp4;codecs=avc1',
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  const supported = window.MediaRecorder?.isTypeSupported;
  if (!supported) return '';
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

// ---- Watermark compositor ------------------------------------------------

// Ask the browser to load the brand webfont so canvas fillText can use it (the
// title already triggers it, but recording may be the first canvas use). Fire and
// forget — the 3s countdown gives it ample time, and serif is a fine fallback.
function preloadWatermarkFont() {
  try { document.fonts?.load(`48px ${WATERMARK_FONT}`); } catch { /* no-op */ }
}

// Draw the watermark in the lower-right, sized relative to the frame so it reads
// the same on phones and desktops. A soft shadow keeps it legible over sky or
// foliage alike.
function drawWatermark(ctx, w, h) {
  const size = Math.max(13, Math.round(h * 0.028));
  const pad = Math.round(size * 1.1);
  ctx.save();
  ctx.font = `${size}px ${WATERMARK_FONT}`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
  ctx.shadowBlur = size * 0.3;
  ctx.shadowOffsetY = 1;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.fillText(WATERMARK_TEXT, w - pad, h - pad);
  ctx.restore();
}

// Build the compositor sized to the WebGL backing store and start the per-frame
// draw loop (WebGL frame + baked-in watermark). `onFrame(compositor)`, if given, is
// called after each composite — the WebCodecs path uses it to encode that frame;
// the MediaRecorder path leaves it out and samples the canvas via captureStream().
// Dimensions are forced even because H.264 requires it (VideoFrame inherits the
// canvas size); harmless for the captureStream path.
function startCompositor(srcCanvas, onFrame) {
  compositor = document.createElement('canvas');
  compositor.width = srcCanvas.width & ~1;
  compositor.height = srcCanvas.height & ~1;
  compCtx = compositor.getContext('2d');

  const drawFrame = () => {
    // Scale source→dest so a mid-clip window resize can't tear the frame.
    compCtx.drawImage(srcCanvas, 0, 0, compositor.width, compositor.height);
    drawWatermark(compCtx, compositor.width, compositor.height);
    if (onFrame) onFrame(compositor);
    drawRAF = requestAnimationFrame(drawFrame);
  };
  drawFrame();
}

// ---- Cinematic camera move -----------------------------------------------

// Drive the camera through an eased orbit + push-in for the duration of the clip.
// We set camera.position each frame around the orbit target; main.js's per-frame
// controls.update() (autoRotate/damping off) re-derives from our position, so it
// preserves the move instead of fighting it. controls input is disabled meanwhile.
function startCameraMove(camera, controls) {
  const target = controls.target.clone();
  const start = new THREE.Spherical().setFromVector3(camera.position.clone().sub(target));
  start.makeSafe();

  const startTime = performance.now();
  const sph = new THREE.Spherical();
  const off = new THREE.Vector3();

  const step = () => {
    const t = Math.min(1, (performance.now() - startTime) / (CLIP_SECONDS * 1000));
    let phi; // computed per shot, ground-safe-clamped once below

    const settleStart = 1 - SETTLE_FRAC;
    if (t < ESTABLISH_FRAC) {
      // ---- Shot 1: establishing. Wide, a touch elevated, from a distinct angle,
      // holding with a slow inward + sideways drift that's still accelerating when
      // the cut hits (ease-in only, no ease-out). Hard-cuts to shot 2.
      const e = easeInSine(t / ESTABLISH_FRAC);
      sph.radius = start.radius * THREE.MathUtils.lerp(ESTABLISH_RADIUS, ESTABLISH_RADIUS_END, e);
      sph.theta = start.theta + THREE.MathUtils.degToRad(ESTABLISH_AZ_DEG + ESTABLISH_DRIFT_DEG * e);
      phi = start.phi - ESTABLISH_LIFT;
    } else if (t < settleStart) {
      // ---- Shot 2: the reveal. Its own normalized timeline s∈[0,1]. Snap to the
      // push-in framing, dolly in, then one continuous sweeping orbit that cranes
      // low. The discontinuity from shot 1's pose to s=0 here IS the edit cut.
      const s = (t - ESTABLISH_FRAC) / (settleStart - ESTABLISH_FRAC);

      // Radius: push-in to the close framing, then hold it for the orbit.
      if (s < PUSH_FRAC) {
        sph.radius = start.radius * THREE.MathUtils.lerp(PUSH_START, DOLLY_NEAR, easeInOutSine(s / PUSH_FRAC));
      } else {
        sph.radius = start.radius * THREE.MathUtils.lerp(DOLLY_NEAR, DOLLY_END, easeInOutSine((s - PUSH_FRAC) / (1 - PUSH_FRAC)));
      }

      // Orbit runs continuously across shot 2 — already building during the push-in,
      // so the close-in and the sweep read as one flowing move (no dead spot).
      sph.theta = start.theta + THREE.MathUtils.degToRad(ORBIT_SWEEP_DEG) * easeInOutSine(s);

      // Vertical: a faint early lift that returns to neutral, then a late crane DOWN
      // to a low, looking-up angle that settles as the orbit ends (smoothstep from
      // DROP_START gives a gentle onset and a soft landing at the bottom).
      const lift = POLAR_RISE * Math.sin(Math.PI * s);
      const drop = THREE.MathUtils.degToRad(POLAR_DROP_DEG) * THREE.MathUtils.smoothstep(s, DROP_START, 1.0);
      phi = start.phi - lift + drop;
    } else {
      // ---- Shot 3: the settle. A calm, composed three-quarter portrait at a
      // restful mid-distance, nearly static — just a whisper of inward + sideways
      // drift so the frame breathes while the foliage/leaf-fall carry the motion.
      // The snap from shot 2's low hero pose to this composed frame IS the edit cut.
      const w = easeInOutSine((t - settleStart) / SETTLE_FRAC);
      sph.radius = start.radius * THREE.MathUtils.lerp(SETTLE_RADIUS, SETTLE_RADIUS_END, w);
      sph.theta = start.theta + THREE.MathUtils.degToRad(SETTLE_AZ_DEG + SETTLE_DRIFT_DEG * w);
      phi = start.phi - SETTLE_LIFT;
    }

    // Ground-safe ceiling on phi (both shots): the largest polar angle that still
    // keeps the camera above MIN_CAM_Y, given the target height and current radius.
    // cam.y = target.y + radius·cos(phi), so phi ≤ acos((MIN_CAM_Y − target.y)/radius).
    // Lets the crane dip well past the horizon for a hero shot without ever punching
    // through the garden floor — and adapts as the dolly changes radius.
    const phiCap = Math.acos(THREE.MathUtils.clamp((MIN_CAM_Y - target.y) / sph.radius, -1, 1));
    sph.phi = THREE.MathUtils.clamp(phi, 0.2, phiCap);

    off.setFromSpherical(sph);
    camera.position.copy(target).add(off);
    camera.lookAt(target);
    cameraRAF = requestAnimationFrame(step);
  };
  step();
}

// ---- Overlay UI (DOM, never captured into the clip) ----------------------

// The countdown panel: a big pulsing number plus Skip/Cancel. Built once, reused.
function ensureCountdownOverlay() {
  let el = document.getElementById('rec-countdown');
  if (el) return el;
  el = document.createElement('div');
  el.id = 'rec-countdown';
  el.innerHTML =
    '<div class="rec-count-hint">Get ready…</div>' +
    '<div class="rec-count-num"></div>' +
    '<div class="rec-count-actions">' +
    '<button type="button" class="rec-btn rec-skip">Skip ⏭</button>' +
    '<button type="button" class="rec-btn rec-cancel">Cancel ✕</button>' +
    '</div>';
  document.body.appendChild(el);
  el.querySelector('.rec-skip').addEventListener('click', beginRecording);
  el.querySelector('.rec-cancel').addEventListener('click', () => abort('🎬 Recording canceled'));
  return el;
}

// Set the number and re-trigger its pop animation (remove → reflow → re-add).
function setCountdownNumber(n) {
  const num = document.getElementById('rec-countdown')?.querySelector('.rec-count-num');
  if (!num) return;
  num.textContent = String(n);
  num.classList.remove('pop');
  void num.offsetWidth; // force reflow so the animation restarts
  num.classList.add('pop');
}

// The "● REC" badge + Cancel button shown during the take. Built once, reused.
function ensureRecBadge() {
  let badge = document.getElementById('rec-badge');
  if (badge) return badge;
  badge = document.createElement('div');
  badge.id = 'rec-badge';
  badge.innerHTML =
    '<span class="rec-dot"></span><span class="rec-label">REC</span>' +
    '<button type="button" class="rec-stop">Cancel ✕</button>';
  document.body.appendChild(badge);
  badge.querySelector('.rec-stop').addEventListener('click', () => abort('🎬 Recording canceled'));
  return badge;
}

function show(el) { el?.classList.add('show'); }
function hide(id) { document.getElementById(id)?.classList.remove('show'); }

// ---- Filename + download -------------------------------------------------

// Tree name for the download filename; sanitized to a safe, lowercase slug.
function clipFilename(ext) {
  const raw = document.getElementById('treeName')?.value?.trim() || 'bonsai';
  const slug = raw.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'bonsai';
  return `${slug}-bonsai.${ext}`;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the click has consumed the URL.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---- Lifecycle -----------------------------------------------------------

// Reset everything to idle: clear timers, stop the stream, restore the camera,
// hide all overlays. Safe to call from any phase.
function cleanup() {
  clearTimeout(countdownTimer); countdownTimer = null;
  clearTimeout(stopTimer); stopTimer = null;
  if (drawRAF) { cancelAnimationFrame(drawRAF); drawRAF = 0; }
  if (cameraRAF) { cancelAnimationFrame(cameraRAF); cameraRAF = 0; }
  if (stream) { stream.getTracks().forEach((t) => t.stop()); stream = null; }
  // WebCodecs teardown: close the encoder (no-op if already flushed) and drop the
  // muxer. finishWebCodecs() handles the success path; this just frees on cancel/end.
  if (encoder) { try { encoder.close(); } catch { /* already closed */ } encoder = null; }
  muxer = null; encodeError = null; backend = null;
  compositor = null; compCtx = null;
  // Drop the render resolution back to what the app uses normally.
  if (prevPixelRatio != null && app.renderer) {
    app.renderer.setPixelRatio(prevPixelRatio);
    prevPixelRatio = null;
  }
  // Restore the user's framing and controls exactly as they were.
  if (restoreControls && app.controls) {
    app.controls.enabled = restoreControls.enabled;
    app.controls.autoRotate = restoreControls.autoRotate;
    if (restoreControls.position && app.camera) {
      app.camera.position.copy(restoreControls.position);
      app.camera.lookAt(app.controls.target);
    }
    app.controls.update();
  }
  restoreControls = null;
  recorder = null;
  hide('rec-countdown');
  hide('rec-badge');
  phase = 'idle';
}

// Cancel from any phase: discard the take (if recording) and reset.
function abort(message) {
  if (phase === 'idle') return;
  canceled = true;
  if (backend === 'wc') {
    cleanup(); // discard the in-progress encode; nothing to finalize
  } else if (recorder && recorder.state !== 'inactive') {
    recorder.stop(); // onstop sees canceled=true and skips the download
  } else {
    cleanup();
  }
  notify(message, 'linear-gradient(45deg, #7f8c8d, #95a5a6)');
}

// Entry point (settings-drawer button). Closes the drawer and runs the countdown.
window.recordBonsai = function () {
  if (phase !== 'idle') return;

  const canvas = app.renderer?.domElement;
  if (!canvas || typeof canvas.captureStream !== 'function' || !window.MediaRecorder) {
    notify('🎬 Recording isn’t supported in this browser', 'linear-gradient(45deg, #c0392b, #e74c3c)');
    return;
  }

  document.getElementById('settingsDrawer')?.classList.remove('open');
  phase = 'countdown';
  canceled = false;
  preloadWatermarkFont(); // warm the brand font during the countdown
  // Kick off the H.264-encode probe now so its verdict is ready when recording
  // starts (only consulted if MediaRecorder can't give us mp4 — i.e. Firefox).
  h264Probe = probeH264Encoder();

  ensureCountdownOverlay();
  show(document.getElementById('rec-countdown'));

  let n = COUNTDOWN_FROM;
  setCountdownNumber(n);
  playSfx('sparkle');

  const tick = () => {
    n -= 1;
    if (n <= 0) { beginRecording(); return; }
    setCountdownNumber(n);
    playSfx('sparkle');
    countdownTimer = setTimeout(tick, 1000);
  };
  countdownTimer = setTimeout(tick, 1000);
};

// Start the actual capture. Called when the countdown finishes or Skip is hit.
// async because picking the backend awaits the H.264 probe, and the WebCodecs path
// awaits the muxer module — both up front, so the scene setup that follows is
// synchronous and can't be interrupted mid-allocation by a Cancel.
async function beginRecording() {
  if (phase !== 'countdown') return; // ignore a late Skip after we've moved on
  clearTimeout(countdownTimer); countdownTimer = null;

  // Pick the capture backend. MediaRecorder mp4 (Chrome/Edge/Safari) is simplest; if
  // it can't do mp4 (Firefox) and the browser can really encode H.264, use WebCodecs;
  // otherwise fall back to MediaRecorder webm.
  const mrMime = pickMimeType();
  const mrGivesMp4 = mrMime.includes('mp4');
  const useWebCodecs = !mrGivesMp4 && await (h264Probe || probeH264Encoder());

  // Preload the muxer (CDN ESM) before touching the scene. If it fails to load we
  // fall back to MediaRecorder webm rather than failing the whole recording.
  let muxerMod = null;
  if (useWebCodecs) {
    try { muxerMod = await import('mp4-muxer'); }
    catch (err) { console.error('mp4-muxer failed to load; falling back to webm:', err); }
  }

  // The awaits above can span a Cancel — bail if we're no longer mid-countdown.
  if (phase !== 'countdown' || canceled) return;
  hide('rec-countdown');

  const renderer = app.renderer;
  const canvas = renderer?.domElement;
  const controls = app.controls;

  // Boost render resolution for the take (restored in cleanup). Compute the pixel
  // ratio that yields ~RECORD_HEIGHT rows from the current CSS height, never below
  // what's already in use and capped at RECORD_MAX_DPR. Must happen BEFORE the
  // compositor reads canvas.width/height so it captures at the higher resolution.
  if (renderer) {
    prevPixelRatio = renderer.getPixelRatio();
    const cssH = canvas.clientHeight || window.innerHeight || canvas.height;
    const recDPR = THREE.MathUtils.clamp(RECORD_HEIGHT / cssH, prevPixelRatio, RECORD_MAX_DPR);
    renderer.setPixelRatio(recDPR);
    // setPixelRatio resizes the drawing buffer but leaves it stale until the next
    // animate() tick — render once now so the compositor's first frame isn't blank.
    if (app.scene && app.camera) renderer.render(app.scene, app.camera);
  }

  // Take over the camera for a cinematic move, remembering the user's framing and
  // control state so cleanup() can put it back exactly as they left it. Input is
  // disabled during the take so a stray drag can't fight the move. Done BEFORE the
  // capture loop starts so the very first recorded frame is already on shot 1.
  if (controls) {
    restoreControls = {
      enabled: controls.enabled,
      autoRotate: controls.autoRotate,
      position: canvas && app.camera ? app.camera.position.clone() : null,
    };
    controls.enabled = false;
    controls.autoRotate = false;
    startCameraMove(app.camera, controls);
  }

  try {
    if (useWebCodecs && muxerMod) startWebCodecsCapture(canvas, muxerMod);
    else startMediaRecorderCapture(canvas, mrMime);
  } catch (err) {
    console.error('Clip recording failed to start:', err);
    notify('🎬 Couldn’t start recording', 'linear-gradient(45deg, #c0392b, #e74c3c)');
    cleanup();
    return;
  }

  phase = 'recording';
  show(ensureRecBadge());
  playSfx('sparkle');
  stopTimer = setTimeout(finishCapture, CLIP_SECONDS * 1000);
}

// The clip's full duration has elapsed — wrap up whichever backend is running.
function finishCapture() {
  if (backend === 'wc') finishWebCodecs();
  else if (recorder && recorder.state !== 'inactive') recorder.stop();
}

// ---- Backend: MediaRecorder (Chrome/Edge/Safari mp4, or webm fallback) ----

function startMediaRecorderCapture(canvas, mimeType) {
  startCompositor(canvas); // no per-frame callback → captureStream samples the canvas
  stream = compositor.captureStream(FPS); // bakes the brand watermark into every frame
  recorder = new MediaRecorder(stream, mimeType ? { mimeType, videoBitsPerSecond: RECORD_BITS_PER_SEC } : undefined);
  backend = 'mr';

  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
  recorder.onstop = () => {
    const wasCanceled = canceled;
    const type = recorder?.mimeType || mimeType || 'video/webm';
    cleanup();
    if (wasCanceled) return; // user bailed — discard, abort() already toasted
    if (!chunks.length) {
      notify('🎬 Recording produced no data', 'linear-gradient(45deg, #c0392b, #e74c3c)');
      return;
    }
    const ext = type.includes('mp4') ? 'mp4' : 'webm';
    triggerDownload(new Blob(chunks, { type }), clipFilename(ext));
    notify('🎬 Clip saved — share your bonsai!', 'linear-gradient(45deg, #16a085, #1abc9c)');
    playSfx('sparkle');
  };
  recorder.start();
}

// ---- Backend: WebCodecs + mp4-muxer (Firefox mp4) -------------------------

// Configure the H.264 encoder + mp4 muxer, then start the compositor loop feeding
// each composited frame to the encoder. Synchronous: the muxer module is preloaded.
function startWebCodecsCapture(canvas, { Muxer, ArrayBufferTarget }) {
  const w = canvas.width & ~1;   // H.264 needs even dimensions; match the compositor
  const h = canvas.height & ~1;

  muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: { codec: 'avc', width: w, height: h, frameRate: FPS },
    fastStart: 'in-memory', // moov atom at the front → seekable/streamable everywhere
    // Rebase timestamps so the first chunk starts at 0 (mp4-muxer requires it).
    // Firefox's encoder warms up over the first frames, so its earliest emitted
    // chunk isn't at t=0; 'offset' shifts them all while keeping the real spacing.
    firstTimestampBehavior: 'offset',
  });
  encoder = new VideoEncoder({
    output: (chunk, meta) => { try { muxer.addVideoChunk(chunk, meta); } catch (e) { encodeError = e; } },
    error: (e) => { encodeError = e; console.error('VideoEncoder error:', e); },
  });
  // `avc: { format: 'avc' }` asks for length-prefixed AVCC chunks carrying an avcC
  // decoder description — what mp4-muxer needs. Firefox can otherwise emit Annex-B
  // (no description), which muxes into a broken file. The probe verifies we really
  // get that description before we commit to this path.
  encoder.configure({
    codec: H264_CODEC, width: w, height: h, bitrate: RECORD_BITS_PER_SEC,
    framerate: FPS, avc: { format: 'avc' },
  });

  wcStartTime = performance.now();
  wcLastTs = -1;
  wcFrameCount = 0;
  backend = 'wc';
  startCompositor(canvas, encodeComposited);
}

// Encode one composited frame. Timestamps are wall-clock microseconds since the
// first frame (the camera move is real-time), kept strictly increasing as the
// encoder requires. A keyframe every ~2s keeps the mp4 seekable.
function encodeComposited(comp) {
  if (!encoder || encoder.state !== 'configured' || encodeError) return;
  let ts = Math.round((performance.now() - wcStartTime) * 1000);
  if (ts <= wcLastTs) ts = wcLastTs + 1;
  wcLastTs = ts;
  let frame = null;
  try {
    // Give the frame a duration: Firefox leaves EncodedVideoChunk.duration null
    // otherwise, and mp4-muxer rejects a null duration. Sync still rides on the
    // (wall-clock) timestamps; duration is just the declared sample length.
    frame = new VideoFrame(comp, { timestamp: ts, duration: FRAME_DUR_US });
    encoder.encode(frame, { keyFrame: wcFrameCount % (FPS * 2) === 0 });
    wcFrameCount += 1;
  } catch (err) {
    encodeError = err;
  } finally {
    frame?.close();
  }
}

// Drain the encoder, finalize the mp4, and download it. Mirrors the MediaRecorder
// onstop path (discard on cancel, toast + download otherwise).
async function finishWebCodecs() {
  if (drawRAF) { cancelAnimationFrame(drawRAF); drawRAF = 0; } // stop queuing new frames
  const wasCanceled = canceled;
  try {
    if (encoder && encoder.state === 'configured') await encoder.flush();
    if (encodeError) throw encodeError;
    muxer.finalize();
    const { buffer } = muxer.target;
    cleanup();
    if (wasCanceled) return; // user bailed — discard, abort() already toasted
    if (!buffer || buffer.byteLength === 0) {
      notify('🎬 Recording produced no data', 'linear-gradient(45deg, #c0392b, #e74c3c)');
      return;
    }
    triggerDownload(new Blob([buffer], { type: 'video/mp4' }), clipFilename('mp4'));
    notify('🎬 Clip saved — share your bonsai!', 'linear-gradient(45deg, #16a085, #1abc9c)');
    playSfx('sparkle');
  } catch (err) {
    console.error('WebCodecs clip failed:', err);
    cleanup();
    if (!wasCanceled) {
      const detail = err?.message ? `: ${err.message}` : '';
      notify(`🎬 Couldn’t encode the clip${detail}`, 'linear-gradient(45deg, #c0392b, #e74c3c)');
    }
  }
}
